import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, Send, RotateCcw, CheckSquare } from 'lucide-react';
import api from '../services/api';

const SESSION_KEY = 'minerva_chat_session_id';
const HISTORY_KEY = 'minerva_chat_history_v2';
// Mensajes de conversaciones ya terminadas: siguen en pantalla para consultar
// lo último que se hizo, pero no se envían a Minerva.
const ARCHIVE_KEY = 'minerva_chat_archived_v1';
const MAX_ARCHIVED_BUBBLES = 200;

const formatoFechaHora = (fecha) => fecha.toLocaleString('es-ES', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

// Convierte el history de Gemini a burbujas mostrables. Solo los parts con
// `text` se muestran; las llamadas a herramientas y sus respuestas quedan
// ocultas.
const historyToBubbles = (history) => {
  if (!Array.isArray(history)) return [];
  const out = [];
  for (const entry of history) {
    if (!entry || !Array.isArray(entry.parts)) continue;
    const text = entry.parts.map(p => (p && typeof p.text === 'string' ? p.text : '')).join('').trim();
    if (text === '') continue;
    if (text === '[INICIO]') continue;
    out.push({ role: entry.role === 'model' ? 'assistant' : 'user', content: text });
  }
  return out;
};

const MinervaChatModal = ({ open, onClose }) => {
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || generateSessionId(); } catch { return generateSessionId(); }
  });
  const [history, setHistory] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [archived, setArchived] = useState(() => {
    try {
      const raw = localStorage.getItem(ARCHIVE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingReply, setPendingReply] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const bubbles = useMemo(() => {
    const list = [...archived, ...historyToBubbles(history)];
    if (pendingUser) list.push({ role: 'user', content: pendingUser });
    if (pendingReply) list.push({ role: 'assistant', content: pendingReply });
    return list;
  }, [archived, history, pendingUser, pendingReply]);

  useEffect(() => {
    try { localStorage.setItem(SESSION_KEY, sessionId); } catch {}
  }, [sessionId]);
  useEffect(() => {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch {}
  }, [history]);
  useEffect(() => {
    try { localStorage.setItem(ARCHIVE_KEY, JSON.stringify(archived)); } catch {}
  }, [archived]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [bubbles, open]);
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    console.log('[minerva-chat] modal open=' + open);
  }, [open]);

  useEffect(() => {
    return () => { console.log('[minerva-chat] component UNMOUNTED'); };
  }, []);

  // Pasa los mensajes de la conversación viva a los archivados, con una línea
  // que dice cuándo terminó. Siguen viéndose; dejan de enviarse a Minerva.
  const archivarConversacion = (motivo) => {
    setArchived((cur) => {
      const cerrados = historyToBubbles(history);
      if (cerrados.length === 0) return cur;
      const lista = [...cur, ...cerrados, {
        role: 'separator',
        content: motivo + ' · ' + formatoFechaHora(new Date()),
      }];
      return lista.length > MAX_ARCHIVED_BUBBLES ? lista.slice(-MAX_ARCHIVED_BUBBLES) : lista;
    });
  };

  const endConversation = async () => {
    if (sending || historyToBubbles(history).length === 0) return;
    archivarConversacion('Conversación terminada');
    setHistory([]);
    setPendingUser(null);
    setPendingReply(null);
    setError('');
    const newId = generateSessionId();
    setSessionId(newId);
    try {
      await api.post('/minerva-chat', { reset: true, session_id: newId });
    } catch {}
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    console.log('[minerva-chat] send: text=' + JSON.stringify(text) + ' history_len=' + history.length);
    setError('');
    setPendingUser(text);
    setPendingReply(null);
    setInput('');
    setSending(true);
    try {
      const res = await api.post('/minerva-chat', {
        message: text,
        session_id: sessionId,
        history,
      });
      console.log('[minerva-chat] response status=' + res.status + ' keys=' + Object.keys(res.data || {}).join(','));
      const data = res.data || {};
      if (data.ok === false) {
        console.warn('[minerva-chat] ok=false error=' + data.error);
        setError(data.error || 'Error de Minerva');
        setPendingUser(null);
        return;
      }
      // El servidor da por terminada la sesión que lleva horas parada: archiva
      // lo anterior (se sigue viendo) y adopta la sesión nueva que devuelve.
      if (data.session_expired) {
        console.log('[minerva-chat] sesión caducada; nueva=' + String(data.session_id || '').slice(0, 12));
        archivarConversacion('Conversación anterior, terminada por inactividad');
        if (data.session_id) setSessionId(data.session_id);
      }
      if (Array.isArray(data.history)) {
        console.log('[minerva-chat] applying history len=' + data.history.length + ' reply_preview=' + String(data.reply || '').slice(0, 80));
        setHistory(data.history);
        setPendingUser(null);
        setPendingReply(null);
      } else if (typeof data.reply === 'string') {
        console.log('[minerva-chat] fallback reply (no history) preview=' + String(data.reply).slice(0, 80));
        setHistory((cur) => [
          ...cur,
          { role: 'user', parts: [{ text }] },
          { role: 'model', parts: [{ text: data.reply }] },
        ]);
        setPendingUser(null);
        setPendingReply(null);
      } else {
        console.warn('[minerva-chat] respuesta sin history ni reply: data=' + JSON.stringify(data).slice(0, 200));
      }
    } catch (e) {
      console.error('[minerva-chat] error catch:', e);
      const msg = e?.response?.data?.error || e?.message || 'Error de red';
      setError(msg);
      setPendingUser(null);
    } finally {
      setSending(false);
      // Devolver el foco al campo de texto tras responder, para seguir
      // escribiendo sin tener que hacer clic. setTimeout(0) espera al re-render
      // que reactiva el textarea (estaba disabled mientras sending=true).
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const reset = async () => {
    setHistory([]);
    setArchived([]);
    setPendingUser(null);
    setPendingReply(null);
    setError('');
    const newId = generateSessionId();
    setSessionId(newId);
    try {
      await api.post('/minerva-chat', { reset: true, session_id: newId });
    } catch {}
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
        zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(92vw, 720px)', height: 'min(90vh, 720px)',
          background: '#fff', borderRadius: 12, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#0f172a', color: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            <MessageCircle size={18} /> Chat con Minerva
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {/* Con la palabra al lado: como icono suelto entre otros dos
                iguales no se distinguía. */}
            <button
              className="btn-icon"
              onClick={endConversation}
              disabled={sending || historyToBubbles(history).length === 0}
              title="Terminar conversación (los mensajes siguen en pantalla)"
              style={{ color: '#fff', gap: 6, padding: '8px 10px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              <CheckSquare size={18} /> Terminar
            </button>
            <button className="btn-icon" onClick={reset} title="Empezar conversación nueva" style={{ color: '#fff' }}>
              <RotateCcw size={18} />
            </button>
            <button className="btn-icon" onClick={onClose} title="Cerrar" style={{ color: '#fff' }}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div ref={scrollRef} style={{
          flex: 1, overflowY: 'auto', padding: 16, background: '#f8fafc',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {bubbles.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 14, textAlign: 'center', marginTop: 20 }}>
              Escribe un mensaje para empezar a hablar con Minerva.
            </div>
          )}
          {bubbles.map((m, i) => (m.role === 'separator' ? (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              color: '#64748b', fontSize: 12, margin: '6px 0',
            }}>
              <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              <span style={{ whiteSpace: 'nowrap' }}>{m.content}</span>
              <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>
          ) : (
            <div key={i} style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              background: m.role === 'user' ? '#1d4ed8' : '#fff',
              color: m.role === 'user' ? '#fff' : '#0f172a',
              border: m.role === 'user' ? 'none' : '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '8px 12px',
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {m.content}
            </div>
          )))}
          {sending && (
            <div style={{ alignSelf: 'flex-start', color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>
              Minerva está pensando…
            </div>
          )}
          {error && (
            <div style={{ alignSelf: 'center', color: '#b91c1c', fontSize: 13, background: '#fee2e2', padding: '6px 10px', borderRadius: 8 }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
          <textarea
            ref={inputRef}
            className="form-input"
            rows={1}
            placeholder="Escribe tu mensaje…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={sending}
            style={{ flex: 1, resize: 'none', minHeight: 40, maxHeight: 120 }}
          />
          <button
            className="btn btn-primary"
            onClick={send}
            disabled={sending || !input.trim()}
            title="Enviar"
            style={{ minWidth: 80 }}
          >
            <Send size={16} /> Enviar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MinervaChatModal;
